import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { onHide, onShow } from '@intercom/messenger-js-sdk';
import { ContactSupport, Menu, MenuOpen } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CardMedia,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  useMediaQuery
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

import CurrentUser from '@/components/CurrentUser';
import FacilitySelector from '@/components/FacilitySelector';
import { EXACARE_LOGO_URLS } from '@/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsLandscapeMobileViewport } from '@/hooks/useIsLandscapeMobileViewport';
import { useSidebarExpandedAtom } from '@/hooks/useSidebarExpandedAtom';
import InboxModal from '@/pages/AdminHub/Staff/Notifications/components/InboxModal';
import { RoutePath } from '@/routes/RoutePath';

import { showResidentGlobalSearchDialog } from '../ResidentGlobalSearchDialog';

import { SidebarItem, useGetSidebarCRMItems } from './getSidebarCRMItems';
import { SidebarItemBadge } from './SidebarItemBadge';

export const StyledNavButton = styled(ListItemButton)({
  minHeight: 58,
  height: 58,
  borderRadius: '100px',
  '&.Mui-disabled': {
    opacity: 1
  },
  '&.Mui-selected': {
    '&:hover': {
      background: '#D3E5F4'
    },
    background: '#D3E5F4'
  }
});

export const StyledNavButtonOutlined = styled(StyledNavButton)({
  border: '1px solid #D4DEE7',
  '& .MuiListItemAvatar-root': {
    margin: '0 4px 0 -8px'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 'unset',
    marginRight: '12px',
    '& .MuiSvgIcon-root': {
      color: '#364955'
    }
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#364955'
  },
  '& .MuiListItemText-secondary': {
    fontSize: '12px!important',
    color: '#667A86'
  },
  '& .MuiTypography-root': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

export const isCurrentPath = (item: SidebarItem, pathname: string) => {
  const isDomainPath =
    pathname.startsWith('/crm') || pathname.startsWith('/ehr');
  const pathbase = (
    pathname.split('/').filter(Boolean)[isDomainPath ? 1 : 0] ?? pathname
  ).toLowerCase();
  const pathtext = (
    item.text.split(' ').filter(Boolean)[0] ?? ''
  ).toLowerCase();

  if (pathname === item.path) {
    return true;
  }

  const itemPathSegments = item.path.split('/').filter(Boolean);
  const pathnameSegments = pathname.split('/').filter(Boolean);

  // Match based on segments, ensuring the pathbase is exactly matched
  // as a whole segment and not just a substring
  if (
    itemPathSegments.length >= 2 &&
    pathnameSegments.length >= 2 &&
    itemPathSegments[0] === pathnameSegments[0] &&
    itemPathSegments[1] === pathbase
  ) {
    return true;
  }

  if (isDomainPath && pathnameSegments.length <= 2) {
    return pathbase === pathtext;
  }

  return false;
};

const Sidebar = () => {
  const currentUser = useCurrentUser().data!;
  const [showIntercom, setShowIntercom] = React.useState(false);
  const isLandscapeMobile = useIsLandscapeMobileViewport();
  const pathname = useLocation().pathname;
  const crmSidebarItems = useGetSidebarCRMItems();
  const isMediaQueryDownLg = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('lg')
  );
  const isMediaQueryDownMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const [expanded, setExpanded] = useSidebarExpandedAtom();
  React.useEffect(() => {
    setExpanded(isMediaQueryDownLg || isMediaQueryDownMd ? false : true);
  }, [isMediaQueryDownLg, isMediaQueryDownMd]);

  useEffect(() => {
    if (showIntercom) {
      window?.Intercom?.('show');
    } else {
      window?.Intercom?.('hide');
    }
    onShow(() => {
      setShowIntercom(true);
    });
    onHide(() => {
      setShowIntercom(false);
    });
  }, [showIntercom]);

  return (
    <Stack
      component="nav"
      sx={{
        width: expanded ? '280px' : '90px',
        transition: 'width 200ms ease-in-out'
      }}
      direction="column"
    >
      <List dense>
        <Stack
          padding="16px 16px 0 16px"
          direction={isMediaQueryDownMd || !expanded ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          gap={expanded ? 1 : 3}
        >
          {isMediaQueryDownMd ? (
            <CardMedia
              component="img"
              src={EXACARE_LOGO_URLS.heart}
              sx={{ width: 30, height: 30, objectFit: 'contain' }}
            />
          ) : (
            <IconButton
              sx={{ padding: expanded ? '8px 8px 8px 15px' : '8px' }}
              edge={expanded ? 'end' : undefined}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <MenuOpen /> : <Menu />}
            </IconButton>
          )}
          {expanded && (
            <Box sx={{ marginLeft: '0 !important' }}>
              <CardMedia component="img" src={EXACARE_LOGO_URLS.horizontal} />
            </Box>
          )}
          <IconButton
            onClick={() => {
              const newState = !showIntercom;
              setShowIntercom(newState);
              if (newState) {
                window?.Intercom?.('show');
              } else {
                window?.Intercom?.('hide');
              }
            }}
            sx={{
              backgroundColor: '#11A4D1',
              color: '#FFFFFF',
              width: '24px',
              height: '24px',
              '& svg': {
                fontSize: '16px'
              },
              '&:hover': {
                backgroundColor: '#0E83A7'
              },
              display: 'flex'
            }}
          >
            <ContactSupport />
          </IconButton>
          {!isLandscapeMobile && (
            <Stack
              sx={{
                '& div': {
                  margin: '1px 0 0 0'
                },
                '&.MuiListItemButton-root:hover': {
                  backgroundColor: 'transparent !important'
                }
              }}
            >
              <InboxModal userId={currentUser.id} />
            </Stack>
          )}
        </Stack>
      </List>
      <Stack
        overflow="auto"
        height={1}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        <List dense>
          {crmSidebarItems.map((item, index) => {
            const { icon, text, path, badgeProps, onClick } = item;
            return (
              <SidebarItemBadge expanded={expanded} key={index} {...badgeProps}>
                <ListItem>
                  <StyledNavButton
                    sx={{
                      height: '58px'
                    }}
                    // @ts-ignore
                    component={onClick ? 'button' : Link}
                    to={onClick ? undefined : path}
                    onClick={onClick}
                    selected={isCurrentPath(item, pathname)}
                  >
                    <ListItemIcon sx={{ minWidth: 'unset' }}>
                      {icon}
                    </ListItemIcon>
                    {expanded && (
                      <>
                        <ListItemText
                          sx={{ paddingLeft: 2 }}
                          primaryTypographyProps={{
                            whiteSpace: 'nowrap',
                            fontSize: '14px',
                            fontWeight: isCurrentPath(item, pathname)
                              ? 700
                              : 500,
                            color: '#364955'
                          }}
                          primary={text}
                        />
                        {path === RoutePath.CRMProspectsResidents && (
                          <ListItemIcon
                            sx={{
                              minWidth: 'unset'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              showResidentGlobalSearchDialog();
                            }}
                          >
                            <SearchIcon sx={{ color: '#9AAEBB !important' }} />
                          </ListItemIcon>
                        )}
                      </>
                    )}
                  </StyledNavButton>
                </ListItem>
              </SidebarItemBadge>
            );
          })}
        </List>
        <List dense>
          <ListItem>
            <FacilitySelector {...{ expanded }} />
          </ListItem>
          <ListItem>
            <CurrentUser {...{ expanded }} />
          </ListItem>
        </List>
      </Stack>
    </Stack>
  );
};

export default Sidebar;
